// Rayon — the territories a scope-limited role is confined to.

import { useState } from 'react';
import { Plus, MapPin } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Chip } from '@/components/ui/Chip';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingState } from '@/components/ui/LoadingState';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { useRayons, useCreateRayon, useUpdateRayon } from '@/hooks/useRayons';
import { usePermissions } from '@/hooks/usePermissions';

export function RayonForm() {
  const { can } = usePermissions();
  const editable = can('settings', 'edit');
  const { data: rayons, isLoading } = useRayons();
  const create = useCreateRayon();
  const update = useUpdateRayon();

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = async () => {
    setError(null);
    try {
      await create.mutateAsync({ code: code.trim().toUpperCase(), name: name.trim() });
      setCode('');
      setName('');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (isLoading) return <LoadingState message="Memuat rayon…" />;

  return (
    <div className="space-y-4">
      <p className="text-small text-ink-subtle leading-relaxed">
        Rayon membatasi apa yang dilihat role bertanda “Batasi per rayon”. User tanpa
        rayon tetap melihat semuanya, dan event tanpa rayon tetap terlihat oleh semua
        orang — jadi mengaktifkan pembatasan tidak langsung menyembunyikan apa pun
        sampai rayon benar-benar diisi.
      </p>

      {error && (
        <div className="p-3 rounded border border-danger/30 bg-danger/5 text-caption text-danger">
          {error}
        </div>
      )}

      {editable && (
        <Card padding="md">
          <div className="flex items-end gap-3 flex-wrap">
            <Input
              label="Kode"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="JKT-01"
              className="w-36 font-mono"
            />
            <Input
              label="Nama rayon"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jakarta Pusat"
              className="flex-1 min-w-[200px]"
            />
            <Button
              variant="primary"
              leftIcon={<Plus size={14} />}
              disabled={!code.trim() || !name.trim()}
              loading={create.isPending}
              onClick={add}
            >
              Tambah
            </Button>
          </div>
        </Card>
      )}

      <Card padding="none">
        {!rayons || rayons.length === 0 ? (
          <EmptyState
            icon={<MapPin size={24} strokeWidth={1.5} />}
            title="Belum ada rayon"
            description="Tanpa rayon, pembatasan per rayon tidak berpengaruh apa-apa."
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Kode</TH>
                <TH>Nama</TH>
                <TH>Status</TH>
                <TH />
              </TR>
            </THead>
            <TBody>
              {rayons.map((r) => (
                <TR key={r.id}>
                  <TD className="font-mono text-small">{r.code}</TD>
                  <TD className="text-small text-ink">{r.name}</TD>
                  <TD>
                    <Chip variant={r.isActive ? 'success' : 'default'}>
                      {r.isActive ? 'Aktif' : 'Nonaktif'}
                    </Chip>
                  </TD>
                  <TD className="text-right">
                    {editable && (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={update.isPending}
                        onClick={() =>
                          update.mutate({ id: r.id, patch: { isActive: !r.isActive } })
                        }
                      >
                        {r.isActive ? 'Nonaktifkan' : 'Aktifkan'}
                      </Button>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
